/**
 * Indicador visual no accionable con el mismo cascarón que PowerButton
 * (tamaño, anillo idle/activo y tipografía del icono).
 * Usado p.ej. por EvoHome (demanda de calor) en el hueco del power.
 */
import React from 'react';
import { Box, IconButton } from '@mui/material';
import { POWER_BUTTON_SIZE } from '../../styles/deviceStyles';
import PowerRing from './PowerRing';

/** Mismos grosores que PowerButton (idle / “encendido”). Active un poco más grueso, solo hacia adentro. */
export const POWER_RING_IDLE_PX = 3.5;
export const POWER_RING_ACTIVE_PX = 5;
export const POWER_ICON_FONT_SIZE = 20;

export interface CardStatusPowerChromeProps {
  /** Equivale a power ON → fondo blanco + anillo grueso de accentColor. */
  active: boolean;
  /** Color del icono y del anillo cuando active (power usa #2391FF). */
  accentColor: string;
  /** Icono central (p.ej. Whatshot). */
  icon: React.ReactNode;
  /** Misma atenuación que PowerButton offline/loading. */
  dimmed?: boolean;
  'aria-label'?: string;
}

/**
 * Réplica estructural de PowerButton (wrapper + IconButton 43×43 + anillo absoluto).
 * Sin onClick / ripple / disabled-greying de MUI.
 */
const CardStatusPowerChrome: React.FC<CardStatusPowerChromeProps> = ({
  active,
  accentColor,
  icon,
  dimmed = false,
  'aria-label': ariaLabel,
}) => {
  const ringBorderWidth = active ? POWER_RING_ACTIVE_PX : POWER_RING_IDLE_PX;
  const ringBorderColor = active ? accentColor : '#fff';

  return (
    <Box
      aria-label={ariaLabel}
      sx={{ position: 'relative', width: POWER_BUTTON_SIZE, height: POWER_BUTTON_SIZE }}
    >
      <Box
        sx={{
          position: 'relative',
          width: POWER_BUTTON_SIZE,
          height: POWER_BUTTON_SIZE,
        }}
      >
        <IconButton
          component="div"
          disableRipple
          tabIndex={-1}
          sx={{
            position: 'relative',
            width: POWER_BUTTON_SIZE,
            height: POWER_BUTTON_SIZE,
            minWidth: POWER_BUTTON_SIZE,
            minHeight: POWER_BUTTON_SIZE,
            maxWidth: POWER_BUTTON_SIZE,
            maxHeight: POWER_BUTTON_SIZE,
            borderRadius: '50%',
            backgroundColor: active ? '#FFFFFF' : '#383838',
            opacity: dimmed ? 0.5 : 1,
            p: 0,
            m: 0,
            boxShadow: 'none',
            boxSizing: 'border-box',
            overflow: 'visible',
            cursor: 'inherit',
            transition: 'background-color 0.3s ease-in-out, opacity 0.3s ease-in-out',
            '&:hover': {
              backgroundColor: active ? '#FFFFFF' : '#383838',
            },
          }}
        >
          {icon}
          {/* Mismo anillo SVG que PowerButton (Ø/grosor hacia adentro, anti-alias nítido). */}
          <PowerRing
            size={POWER_BUTTON_SIZE}
            borderWidth={ringBorderWidth}
            color={ringBorderColor}
          />
        </IconButton>
      </Box>
    </Box>
  );
};

export default React.memo(CardStatusPowerChrome);
